import { Stack } from "@fluentui/react";
import { Tooltip } from "@fluentui/react-components";
import {
    AlertBadge24Filled,
    ChartMultiple24Regular,
    ChatAdd28Regular,
    Checkmark24Filled,
    NavigationFilled,
    PersonFeedback28Regular,
    QuestionCircle24Filled,
    PhoneMultipleSettings24Filled,
    Settings28Regular
} from "@fluentui/react-icons";

import { AutoStories, Logout, MenuBook, RateReview, SwapHoriz, TrackChanges } from "@mui/icons-material";
import { Box, Divider, IconButton, ListItemIcon, Menu, MenuItem } from "@mui/material";
import Cookies from "js-cookie";
import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Locale, saveUserDetails } from "../../api";
import termsAndConditionsImg from "../../assets/terms-and-condition.png";
import localeChangeImg from "../../assets/translation.png";
import { appInfo, supportEnvHost, userInfo, isAdmin, userSupportId, isEvaluator, isViewer, isConsultant, userRole } from "../../authConfig";
import i18next from "../../i18n/i18n";
import styles from "./Layout.module.css";
import HeaderInfo from "./HeaderInfo";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Store";
type TokenValues = {
    isInchargeOfAiWorks: string;
    userSupportId: string;
};

interface Props {
    callback: () => void;
    startTour: (start: boolean) => void;
}

type MenuRole = "admin" | "consultant" | "evaluatorViewer" | "user";

const Layout = ({ callback, startTour }: Props) => {
    const dispatch = useDispatch();
    const localeStr: string = userInfo.locale || Locale.JA;
    sessionStorage.setItem("selectedLocale", userInfo.locale || Locale.JA);
    // Type assertion
    const localeEnum: Locale = localeStr as Locale;
    const [locale, setLocale] = useState<Locale>(localeEnum);
    const [menuItemLists, setMenuItemLists] = useState<any>([]);
    const [menuSecondItemLists, setMenuSecondItemLists] = useState<any>([]);
    const submenuRef = useRef<HTMLDivElement>(null);
    const [openSubMenu, setOpenSubMenu] = React.useState(false);
    const [anchorSubMenu, setAnchorSubMenu] = React.useState<null | HTMLElement>(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const isAnswerLoading = useSelector((store: RootState) => store.chatComponent.isAnswerLoading);
    const fileUploadOrDeleteAPITriggerActive = useSelector((store: RootState) => store.questionInput.fileUploadOrDeleteAPITriggerActive);
    const isMenuDisabled = Boolean(fileUploadOrDeleteAPITriggerActive?.triggerActive || isAnswerLoading);

    const locales = appInfo.locales;

    const localeOptions = Object.keys(locales).map(key => ({
        key: key.toLowerCase(),
        text: locales[key],
        selected: locale === key,
        data: key
    }));
    const onLocaleChange = (option: any) => {
        handleClose();
        setLocale(option?.data ?? Locale.JA);
        sessionStorage.setItem("selectedLocale", option?.data ?? Locale.JA);
        saveUserSettings({ locale: option?.data ?? Locale.JA }, true);
        userInfo.locale = option?.data ?? Locale.JA;
    };

    const saveUserSettings = async (settings: object, reloadNeeded?: boolean) => {
        try {
            await saveUserDetails(settings)
                .then(response => {
                    return response.json();
                })
                .then(() => {
                    if (reloadNeeded) {
                        window.location.reload();
                    }
                })
                .catch(error => {
                    console.error("Error saving user data:", error);
                });
        } catch (error) {
            console.error("Error saving user data:", error);
        }
    };

    const handleClick = () => {
        callback();
    };
    const handleLogout = () => {
        Cookies.remove("token", { path: "/" });
        Cookies.remove("token", { path: "/assistant" });
        localStorage.setItem("disclaimerHide", "initial");
        window.location.href = supportEnvHost.split("/aiworks.do")[0] + "/view/wap/top";
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        if (isMenuDisabled) return;
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleTourStart = () => {
        if (fileUploadOrDeleteAPITriggerActive?.triggerActive) return;
        startTour(true);
    };

    useEffect(() => {
        const menuListItemValues: any = [
            {
                key: i18next.t("layout.chat"),
                visibleTo: ["admin", "consultant", "evaluatorViewer"],
                item: (
                    <MenuItem
                        key={i18next.t("layout.chat")}
                        onClick={() => {
                            handleClick();
                            handleClose();
                        }}
                        component={NavLink}
                        to="/assistant/chat"
                    >
                        <ListItemIcon>
                            <ChatAdd28Regular style={{ backgroundColor: "none", color: "#3a81d4" }} />
                        </ListItemIcon>
                        {i18next.t("layout.chat")}
                    </MenuItem>
                )
            },
            {
                key: i18next.t("layout.feedbacks"),
                visibleTo: ["admin", "consultant", "evaluatorViewer"],
                item: (
                    <MenuItem
                        key={i18next.t("layout.feedbacks")}
                        onClick={() => {
                            handleClose();
                        }}
                        component={NavLink}
                        to="/assistant/feedbacks"
                    >
                        <ListItemIcon>
                            <PersonFeedback28Regular style={{ backgroundColor: "none", color: "#3a81d4" }} />
                        </ListItemIcon>
                        {i18next.t("layout.feedbacks")}
                    </MenuItem>
                )
            },
            {
                key: i18next.t("layout.reports"),
                visibleTo: ["admin", "consultant"],
                item: (
                    <MenuItem
                        key={i18next.t("layout.reports")}
                        onClick={() => {
                            handleClose();
                        }}
                        component={NavLink}
                        to="/assistant/reports"
                    >
                        <ListItemIcon>
                            <ChartMultiple24Regular style={{ backgroundColor: "none", color: "#3a81d4" }} />
                        </ListItemIcon>
                        {i18next.t("layout.reports")}
                    </MenuItem>
                )
            },
            {
                key: i18next.t("layout.settings"),
                visibleTo: ["admin"],
                item: (
                    <MenuItem
                        key={i18next.t("layout.settings")}
                        onClick={() => {
                            handleClose();
                        }}
                        component={NavLink}
                        to="/assistant/settings"
                    >
                        <ListItemIcon>
                            <Settings28Regular style={{ backgroundColor: "none", color: "#3a81d4" }} />
                        </ListItemIcon>
                        {i18next.t("layout.settings")}
                    </MenuItem>
                )
            },
            {
                key: i18next.t("layout.masterSettings"),
                visibleTo: ["admin"],
                item: (
                    <MenuItem
                        key={i18next.t("layout.masterSettings")}
                        onClick={() => {
                            handleClose();
                        }}
                        component={NavLink}
                        to="/assistant/master-settings"
                    >
                        <ListItemIcon>
                            <PhoneMultipleSettings24Filled style={{ backgroundColor: "none", color: "#3a81d4" }} />
                        </ListItemIcon>
                        {i18next.t("layout.masterSettings")}
                    </MenuItem>
                )
            },
            {
                key: i18next.t("layout.productInstructions"),
                visibleTo: ["admin"],
                item: (
                    <MenuItem
                        key={i18next.t("layout.productInstructions")}
                        onClick={() => {
                            handleClose();
                        }}
                        component={NavLink}
                        to="/assistant/product-instructions"
                    >
                        <ListItemIcon>
                            <TrackChanges style={{ backgroundColor: "none", color: "#3a81d4" }} />
                        </ListItemIcon>
                        {i18next.t("layout.productInstructions")}
                    </MenuItem>
                )
            },
            {
                key: i18next.t("layout.customerPtfVersions"),
                visibleTo: ["admin"],
                item: (
                    <MenuItem
                        key={i18next.t("layout.customerPtfVersions")}
                        onClick={() => {
                            handleClose();
                        }}
                        component={NavLink}
                        to="/assistant/customer-ptf-versions"
                    >
                        <ListItemIcon>
                            <SwapHoriz style={{ backgroundColor: "none", color: "#3a81d4" }} />
                        </ListItemIcon>
                        {i18next.t("layout.customerPtfVersions")}
                    </MenuItem>
                )
            },
            {
                key: i18next.t("layout.updates"),
                visibleTo: ["admin", "consultant", "evaluatorViewer", "user"],
                item: (
                    <div>
                        <MenuItem
                            key={i18next.t("layout.updates")}
                            onClick={() => {
                                handleClose();
                            }}
                            target="_blank"
                            component={NavLink}
                            to="/assistant/updates"
                        >
                            <ListItemIcon>
                                <AlertBadge24Filled style={{ backgroundColor: "none", color: "#3a81d4" }} />
                            </ListItemIcon>
                            {i18next.t("layout.updates")}
                        </MenuItem>
                    </div>
                )
            }
        ];
        const menuRole: MenuRole = isAdmin ? "admin" : isConsultant ? "consultant" : isEvaluator || isViewer ? "evaluatorViewer" : "user";
        const itemsToSet = menuListItemValues.filter((item: { visibleTo: MenuRole[] }) => item.visibleTo.includes(menuRole));
        setMenuItemLists(itemsToSet);
        const secondMenuList = [
            {
                key: i18next.t("chat.disclaimerHeader2"),
                item: (
                    <div>
                        <Divider />
                        <MenuItem
                            key={i18next.t("chat.disclaimerHeader2")}
                            onClick={() => {
                                handleClose();
                            }}
                            target="_blank"
                            component={NavLink}
                            to="/assistant/terms"
                        >
                            <img src={termsAndConditionsImg} style={{ width: 28, height: 28, marginRight: "8px" }} onClick={event => event.stopPropagation()} />
                            {i18next.t("chat.disclaimerHeader2")}
                        </MenuItem>
                    </div>
                )
            },
            {
                key: i18next.t("chat.guideHeader2"),
                item: (
                    <div>
                        <MenuItem
                            key={i18next.t("chat.guideHeader2")}
                            onClick={() => {
                                handleClose();
                            }}
                            target="_blank"
                            component={NavLink}
                            to="/assistant/guide"
                        >
                            <ListItemIcon>
                                <AutoStories style={{ backgroundColor: "none", color: "#3a81d4" }} />
                            </ListItemIcon>
                            {i18next.t("chat.guideHeader2")}
                        </MenuItem>
                    </div>
                )
            },
            {
                key: i18next.t("layout.logoutButtonLabel"),
                item: (
                    <>
                        <Divider />
                        <MenuItem
                            key={i18next.t("layout.logoutButtonLabel")}
                            onClick={() => {
                                handleClose();
                                handleLogout();
                            }}
                        >
                            <ListItemIcon>
                                <Logout style={{ backgroundColor: "none", color: "#3a81d4" }} />
                            </ListItemIcon>
                            {i18next.t("layout.logoutButtonLabel")}
                        </MenuItem>
                    </>
                )
            }
        ];
        setMenuSecondItemLists([...secondMenuList]);
    }, [isAdmin, isConsultant, isEvaluator, isViewer]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (submenuRef.current && !submenuRef.current.contains(event.target as Node)) {
                setOpenSubMenu(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const resolvedRole = userRole && userRole !== "" ? userRole : !isAdmin ? "User" : "Admin";

    return (
        <Box className={styles.layout}>
            <header className={styles.header} role={"banner"}>
                <div className={styles.headerContainer}>
                    <HeaderInfo onNavigateChat={handleClick} />
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", marginLeft: "auto" }}>
                        <div style={{ paddingRight: "5px", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                            {userInfo?.isWap === "True" && (
                                <>
                                    <div style={{ marginTop: "8px", marginLeft: "10px" }}>
                                        <Link
                                            style={{ textDecoration: "underline", alignSelf: "center" }}
                                            to=""
                                            onClick={() => {
                                                window.open(
                                                    "https://docs.google.com/spreadsheets/d/1p-MiAJxyiOjYwd-bTNjx152GmVfBXF8XvyIsK7q8aM0/edit?usp=sharing",
                                                    "_blank"
                                                );
                                            }}
                                        >
                                            <Tooltip
                                                content={{
                                                    children: i18next.t("layout.manual"),
                                                    style: {
                                                        backgroundColor: "white"
                                                    }
                                                }}
                                                relationship="label"
                                            >
                                                <MenuBook style={{ backgroundColor: "none", color: "white" }} />
                                            </Tooltip>
                                        </Link>
                                    </div>
                                    <div style={{ marginTop: "10px", marginLeft: "10px", marginRight: "10px" }}>
                                        <Link
                                            style={{ textDecoration: "underline", alignSelf: "center" }}
                                            to=""
                                            onClick={() => {
                                                window.open(
                                                    "https://docs.google.com/forms/d/e/1FAIpQLSd5GOsfVU7SriBrt3UAJhT-5UApFapLWa8HUnMd3GkvlppdhA/viewform?usp=sf_link",
                                                    "_blank"
                                                );
                                            }}
                                        >
                                            <Tooltip
                                                content={{
                                                    children: i18next.t("layout.feedbackForm"),
                                                    style: {
                                                        backgroundColor: "white"
                                                    }
                                                }}
                                                relationship="label"
                                            >
                                                <RateReview style={{ backgroundColor: "none", color: "white" }} />
                                            </Tooltip>
                                        </Link>
                                    </div>
                                </>
                            )}
                            <div style={{ marginTop: "8px", cursor: "pointer" }}>
                                <Tooltip
                                    content={{
                                        children: i18next.t("appTour.tourIcon"),
                                        style: {
                                            backgroundColor: "white"
                                        }
                                    }}
                                    relationship="label"
                                >
                                    <QuestionCircle24Filled
                                        onClick={handleTourStart}
                                        style={{
                                            backgroundColor: "none",
                                            color: "white",
                                            cursor: fileUploadOrDeleteAPITriggerActive?.triggerActive ? "not-allowed" : "pointer"
                                        }}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                        <h3 style={{ marginRight: "8px" }} className={styles.headerTitle}>
                            {userSupportId} {resolvedRole !== "User" && `(${i18next.t(`layout.role.${resolvedRole}`)})`}
                        </h3>
                        {
                            <React.Fragment>
                                <IconButton
                                    sx={{
                                        marginTop: "8px",
                                        cursor: isMenuDisabled ? "not-allowed" : "pointer"
                                    }}
                                    onClick={handleMenuClick}
                                    disabled={isMenuDisabled}
                                >
                                    <Tooltip
                                        content={{
                                            children: i18next.t("layout.screenMenuIconLabel"),
                                            style: {
                                                backgroundColor: "white"
                                            }
                                        }}
                                        relationship="label"
                                    >
                                        <NavigationFilled style={{ cursor: "pointer", color: "white", background: "none", fontSize: "1.5rem" }} />
                                    </Tooltip>
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    id="account-menu"
                                    open={open}
                                    onClose={() => {
                                        setOpenSubMenu(false);
                                        handleClose();
                                    }}
                                    onClick={handleClose}
                                    slotProps={{
                                        paper: {
                                            elevation: 0,
                                            sx: {
                                                overflow: "visible",
                                                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                                                mt: 1.5,
                                                "& .MuiAvatar-root": {
                                                    width: 32,
                                                    height: 32,
                                                    ml: -0.5,
                                                    mr: 1
                                                },
                                                "&::before": {
                                                    content: '""',
                                                    display: "block",
                                                    position: "absolute",
                                                    top: 0,
                                                    right: 14,
                                                    width: 10,
                                                    height: 10,
                                                    bgcolor: "background.paper",
                                                    transform: "translateY(-50%) rotate(45deg)",
                                                    zIndex: 0
                                                }
                                            }
                                        }
                                    }}
                                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                                >
                                    {menuItemLists.map((menuItem: any) => (
                                        <div style={{ fontFamily: "inherit" }} key={menuItem.key}>
                                            {menuItem.item}
                                        </div>
                                    ))}
                                    <div style={{ fontFamily: "inherit" }} key={i18next.t("chat.language")}>
                                        <Divider />
                                        <MenuItem
                                            onClick={event => {
                                                event.stopPropagation();
                                                setAnchorSubMenu(event.currentTarget);
                                                setOpenSubMenu(prev => !prev);
                                            }}
                                        >
                                            <Stack horizontal horizontalAlign="start" style={{ width: "100%" }}>
                                                <img src={localeChangeImg} style={{ width: 28, height: 28, marginRight: "8px" }} />
                                                <span>{i18next.t("chat.language")}</span>
                                            </Stack>
                                        </MenuItem>

                                        {/* Submenu */}
                                        <Menu
                                            anchorEl={anchorSubMenu}
                                            open={openSubMenu}
                                            onClose={() => setOpenSubMenu(false)}
                                            anchorOrigin={{ horizontal: "left", vertical: "top" }}
                                            transformOrigin={{ horizontal: "right", vertical: "top" }}
                                            sx={{
                                                "& .MuiPaper-root": {
                                                    minWidth: "200px",
                                                    boxShadow: "0px 2px 8px rgba(0,0,0,0.32)",
                                                    padding: "0px"
                                                }
                                            }}
                                            ref={submenuRef}
                                        >
                                            {localeOptions.map(option => (
                                                <MenuItem
                                                    key={option.key}
                                                    onClick={() => {
                                                        if (locale !== option.key) {
                                                            onLocaleChange(option);
                                                        }
                                                        setOpenSubMenu(false);
                                                    }}
                                                >
                                                    <Box sx={{ width: "30px" }}>
                                                        {locale === option.key ? (
                                                            <Checkmark24Filled style={{ backgroundColor: "none", color: "green" }} />
                                                        ) : null}
                                                    </Box>
                                                    <span>
                                                        {locale === "ja"
                                                            ? option.key === "en"
                                                                ? i18next.t("layout.enginja")
                                                                : option.text
                                                            : option.key === "ja"
                                                              ? i18next.t("layout.jaineng")
                                                              : option.text}
                                                    </span>
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </div>
                                    {menuSecondItemLists.map((menuItem: any) => (
                                        <div style={{ fontFamily: "inherit" }} key={menuItem.key}>
                                            {menuItem.item}
                                        </div>
                                    ))}
                                </Menu>
                            </React.Fragment>
                        }
                    </Box>
                </div>
            </header>

            <Outlet />
        </Box>
    );
};

export default Layout;
